import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/shared/api/queryKeys';
import {
  addSubscriber,
  createList,
  deleteList,
  fetchSubscriberLists,
  fetchRms,
  fetchSubscribers,
  importClients,
  removeSubscriber,
  renameList,
  type SubscriberParams,
} from '../api/subscribersApi';

export function useSubscriberListsQuery({ mine = false }: { mine?: boolean } = {}) {
  return useQuery({ queryKey: [...queryKeys.subscribers.lists(), { mine }], queryFn: ({ signal }) => fetchSubscriberLists(signal, mine) });
}

/** RMs who can own a client book; only asked for when an admin opens New list. */
export function useRmsQuery(enabled: boolean) {
  return useQuery({ queryKey: [...queryKeys.users.all, 'rms'], queryFn: ({ signal }) => fetchRms(signal), enabled, staleTime: 5 * 60_000, select: (p) => p.items });
}

export function useSubscribersQuery(params: SubscriberParams) {
  return useQuery({
    queryKey: queryKeys.subscribers.list(params),
    queryFn: ({ signal }) => fetchSubscribers(params, signal),
    enabled: params.listId !== '',
    placeholderData: keepPreviousData,
  });
}

export function useSubscriberMutations() {
  const qc = useQueryClient();
  const onSuccess = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.subscribers.all });
    void qc.invalidateQueries({ queryKey: queryKeys.users.all }); // an admin's view of the RM, and its counts
  };
  return {
    add: useMutation({ mutationFn: addSubscriber, onSuccess }),
    remove: useMutation({ mutationFn: removeSubscriber, onSuccess }),
    createList: useMutation({ mutationFn: createList, onSuccess }),
    renameList: useMutation({ mutationFn: renameList, onSuccess }),
    deleteList: useMutation({ mutationFn: deleteList, onSuccess }),
    importClients: useMutation({ mutationFn: importClients, onSuccess }),
  };
}
